import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageShimmer from './ImageShimmer';
import InitialsAvatar from './InitialsAvatar';
import { font } from '../../utils/fontHelper';
import type { Story } from '../../services/storiesService';

const TMDB_POSTER_BASE = 'https://image.tmdb.org/t/p/w185';
const POSTER_SIZE = 80;
const OFFSET = 14;
const RING_WIDTH = 2.5;
const GAP = 5;
const ADD_BADGE_SIZE = 28;

interface YourStoryTileProps {
  ownStories: Story[];
  avatarUrl?: string;
  userName?: string;
  onViewStories?: () => void;
  onAddStory: () => void;
}

const YourStoryTile: React.FC<YourStoryTileProps> = ({
  ownStories,
  avatarUrl,
  userName,
  onViewStories,
  onAddStory,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const hasStories = ownStories.length > 0;
  const visiblePosters = ownStories.slice(0, 4);
  const extraCount = ownStories.length - 4;
  const hasMore = extraCount > 0;
  const stackWidth = hasStories
    ? POSTER_SIZE + (visiblePosters.length - 1) * OFFSET
    : POSTER_SIZE;

  const bgColor = isDark ? '#111' : '#fff';
  const seenColor = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)';

  const handleAddTap = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    onAddStory();
  };

  // Plain "Your story" label — mirrors the other-user rail items.
  const label = (
    <Typography
      sx={{
        fontFamily: font(),
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 400,
        maxWidth: stackWidth + 8,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        lineHeight: 1.2,
      }}
    >
      Your spotlight
    </Typography>
  );

  if (!hasStories) {
    // No active stories: large avatar circle + "+" FAB badge, plain "Your story" text below
    return (
      <Box
        component="button"
        onClick={onAddStory}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 2,
          width: POSTER_SIZE,
          minWidth: 72,
          flexShrink: 0,
          '&:active': { transform: 'scale(0.95)' },
          transition: 'transform 0.1s ease',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Box sx={{ position: 'relative', width: POSTER_SIZE, height: POSTER_SIZE }}>
          {/* Avatar circle — no story ring */}
          <Box
            sx={{
              width: POSTER_SIZE,
              height: POSTER_SIZE,
              borderRadius: '50%',
              border: `2px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
              overflow: 'hidden',
            }}
          >
            <InitialsAvatar
              src={avatarUrl}
              name={userName}
              size={POSTER_SIZE}
              sx={{ width: POSTER_SIZE, height: POSTER_SIZE }}
            />
          </Box>

          {/* "+" FAB badge bottom-right */}
          <Box
            component="button"
            onClick={handleAddTap}
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: ADD_BADGE_SIZE,
              height: ADD_BADGE_SIZE,
              borderRadius: '50%',
              bgcolor: isDark ? '#3A3A3C' : '#2C2C2E',
              border: `2.5px solid ${bgColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              minWidth: 32,
              minHeight: 32,
              zIndex: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            }}
          >
            <AddIcon sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: font(),
            fontSize: 12,
            color: theme.palette.text.secondary,
            fontWeight: 400,
            maxWidth: POSTER_SIZE + 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          Your spotlight
        </Typography>
      </Box>
    );
  }

  // Has stories: poster stack + "+" action badge + chip below
  return (
    <Box
      component="button"
      onClick={onViewStories}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        p: 0.5,
        borderRadius: 2,
        width: stackWidth,
        minWidth: 72,
        flexShrink: 0,
        '&:active': { transform: 'scale(0.95)' },
        transition: 'transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Box sx={{ position: 'relative', width: stackWidth, height: POSTER_SIZE }}>
        {visiblePosters.map((story, i) => {
          const zIndex = visiblePosters.length - i;
          const rawPoster = story.kind === 'watchlist' ? story.coverPath : story.posterPath;
          const posterUrl = rawPoster ? `${TMDB_POSTER_BASE}${rawPoster}` : null;
          const altText = story.kind === 'watchlist' ? story.watchlistName : story.movieTitle;

          return (
            <Box
              key={story.id}
              sx={{
                position: 'absolute',
                left: i * OFFSET,
                top: 0,
                width: POSTER_SIZE,
                height: POSTER_SIZE,
                borderRadius: '50%',
                zIndex,
                background: seenColor,
                padding: `${RING_WIDTH}px`,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: bgColor,
                  padding: `${GAP}px`,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  }}
                >
                  {posterUrl ? (
                    <ImageShimmer
                      src={posterUrl}
                      alt={altText}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: 18 }}>🎬</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* +N badge — bottom-right */}
        {hasMore && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              bgcolor: isDark ? 'rgba(40,32,60,0.92)' : 'rgba(240,236,255,0.92)',
              border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '10px',
              px: '5px',
              py: '1px',
              zIndex: visiblePosters.length + 1,
              lineHeight: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: font(),
                fontSize: 10,
                fontWeight: 700,
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)',
              }}
            >
              +{extraCount}
            </Typography>
          </Box>
        )}

        {/* "+" action badge — bottom-left when +N shown, bottom-right otherwise */}
        <Box
          component="button"
          onClick={handleAddTap}
          sx={{
            position: 'absolute',
            ...(hasMore ? { left: 0 } : { right: 0 }),
            bottom: -2,
            width: ADD_BADGE_SIZE,
            height: ADD_BADGE_SIZE,
            borderRadius: '50%',
            bgcolor: isDark ? '#3A3A3C' : '#2C2C2E',
            border: `2.5px solid ${bgColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            minWidth: 32,
            minHeight: 32,
            zIndex: visiblePosters.length + 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          <AddIcon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
      </Box>

      {/* "Your story" plain text below */}
      {label}
    </Box>
  );
};

export default YourStoryTile;
